import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Sparkles, ShieldCheck } from 'lucide-react';
import { useZakatGlobal } from '../../context/ZakatGlobalContext';
import { tSurf } from '../../config/newSurfacesTranslations';

/**
 * QuickZakatCalculator — Bento hero embed
 *
 * A compact, single-panel Zakat check that lives directly inside the hero
 * so users can start calculating without scrolling. Writes to the same
 * ZakatGlobalContext store the full calculator uses, so any values entered
 * here carry over when the user opens the full step-by-step flow below.
 *
 * Fields (deliberately minimal, works across all person-types):
 *   • Cash & savings (currency)
 *   • Gold (grams, 24K)
 *   • Silver (grams, 999)
 *   • Short-term debts (currency)
 *
 * Result renders live (Zakat due / Below Nisab) using calculateZakat() from
 * context so the number matches the full calculator to the cent.
 */
export const QuickZakatCalculator = ({ onOpenFull, testid = 'quick-zakat-calc' }) => {
  const navigate = useNavigate();
  const {
    selectedCountry,
    selectedLanguage,
    assetValues,
    deductionValues,
    updateAssetValue,
    updateDeductionValue,
    calculateZakat,
    formatCurrency,
    effectiveGoldPrice,
    nisabStandard,
    setNisabStandard,
    pricesLoading,
    goldPrice,
    t,
  } = useZakatGlobal();

  // Example seed — pre-fill with realistic sample amounts on first load so
  // the widget renders a non-zero Zakat number instead of a lifeless blank.
  // GATED on live gold/silver prices being loaded — otherwise the widget
  // briefly renders ₹0/g at the top-right and a $0 Zakat total until the
  // rates arrive (P9). We watch `pricesLoading` so the seed fires the
  // moment prices arrive (and only once, tracked by `seededRef`).
  const [isExample, setIsExample] = React.useState(false);
  const seededRef = React.useRef(false);
  React.useEffect(() => {
    if (seededRef.current) return;
    if (pricesLoading || !goldPrice) return; // wait for live rates
    const anyValue =
      Number(assetValues.cash || 0) + Number(assetValues.savings || 0) +
      Number(assetValues.gold || 0) + Number(assetValues.silver || 0) +
      Number(deductionValues.shortTermDebts || 0);
    if (anyValue === 0) {
      updateAssetValue('cash', 12000);
      updateAssetValue('gold', 30);
      updateAssetValue('silver', 100);
      updateDeductionValue('shortTermDebts', 500);
      setIsExample(true);
    }
    seededRef.current = true;
  }, [pricesLoading, goldPrice, assetValues.cash, assetValues.savings, assetValues.gold, assetValues.silver, deductionValues.shortTermDebts, updateAssetValue, updateDeductionValue]);
  const clearExampleFlag = () => { if (isExample) setIsExample(false); };

  const isRTL = selectedLanguage?.dir === 'rtl';
  const lang = selectedLanguage?.code || 'en';
  const ts = (p) => tSurf(lang, p);
  const results = calculateZakat();

  // Reuse existing context keys so values persist into the full calculator.
  // For the compact widget we treat "Cash & savings" as one aggregate bucket.
  // Read → sum(cash + savings) so nothing is hidden from the user.
  // Write → route the entire aggregate into `cash` so `savings` (if the user
  // had entered it in the full calc) is never silently mutated.
  const cashAgg = Number(assetValues.cash || 0) + Number(assetValues.savings || 0);
  const cash = cashAgg > 0 ? String(cashAgg) : '';
  const gold = assetValues.gold || '';
  const silver = assetValues.silver || '';
  const debts = deductionValues.shortTermDebts || '';

  return (
    <div
      data-testid={testid}
      className="relative w-full rounded-2xl border border-gold-500/25 bg-[#F3EFEA] backdrop-blur-sm shadow-[0_24px_60px_-24px_rgba(6,49,36,0.55)] overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-stone-200 bg-gradient-to-r from-[#FAF8F5] to-gold-50/60">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex w-7 h-7 rounded-full bg-[#EDF5F1] text-[#0D4733] items-center justify-center shrink-0">
            <Sparkles className="w-3.5 h-3.5" />
          </span>
          <div className="min-w-0">
            <p className="font-heading text-[15px] font-semibold text-[#0D1520] leading-tight truncate">
              {ts('quickCalc.title')}
              {isExample && (
                <span
                  data-testid="quick-example-badge"
                  className="ms-2 inline-flex items-center rounded-full bg-gold-100 text-gold-800 border border-gold-300 text-[10px] font-semibold px-1.5 py-0.5 align-middle"
                  title="Example values — start typing to override with your own."
                >
                  Example
                </span>
              )}
            </p>
            <p className="text-[12px] text-stone-500 leading-tight">
              {selectedCountry.flag} {selectedCountry.name} · {selectedCountry.currency}
            </p>
          </div>
        </div>
        <div className="text-end shrink-0">
          <p className="text-[11px] text-stone-500 leading-tight">{ts('quickCalc.live24k')}</p>
          <p
            className="text-[12px] font-semibold text-[#0D1520] tabular-nums leading-tight"
            data-testid="quick-live-price"
          >
            {pricesLoading || !goldPrice
              ? (<span className="inline-block h-3 w-10 rounded bg-stone-200 animate-pulse align-middle" aria-label="Loading live gold price" />)
              : `${formatCurrency(effectiveGoldPrice)}/g`}
          </p>
        </div>
      </div>

      {/* Fields */}
      <div className="p-4 space-y-3">
        {/* Cash & Savings row */}
        <div className="grid grid-cols-2 gap-2.5">
          <QuickField
            testid="quick-cash"
            label={ts('quickCalc.cash')}
            symbol={selectedCountry.currencySymbol}
            value={cash}
            onChange={(v) => {
              clearExampleFlag();
              updateAssetValue('cash', v);
              if (assetValues.savings) updateAssetValue('savings', 0);
            }}
            isRTL={isRTL}
          />
          <QuickField
            testid="quick-debts"
            label={ts('quickCalc.debts')}
            symbol={selectedCountry.currencySymbol}
            value={debts}
            onChange={(v) => { clearExampleFlag(); updateDeductionValue('shortTermDebts', v); }}
            isRTL={isRTL}
            tone="deduct"
          />
        </div>

        {/* Gold & Silver row */}
        <div className="grid grid-cols-2 gap-2.5">
          <QuickField
            testid="quick-gold"
            label={ts('quickCalc.gold')}
            suffix="g"
            value={gold}
            onChange={(v) => { clearExampleFlag(); updateAssetValue('gold', v); }}
            isRTL={isRTL}
            step="0.01"
          />
          <QuickField
            testid="quick-silver"
            label={ts('quickCalc.silver')}
            suffix="g"
            value={silver}
            onChange={(v) => updateAssetValue('silver', v)}
            isRTL={isRTL}
            step="0.01"
          />
        </div>

        {/* Nisab plain-language explanation — always visible so first-time
            users understand what the toggle below controls. */}
        <p
          data-testid="quick-nisab-explanation"
          className="pt-1 text-[11.5px] leading-snug text-stone-600"
        >
          <span className="font-semibold text-[#0D1520]">Nisab</span> = the minimum savings before Zakat is owed.
        </p>

        {/* Nisab toggle — tiny 2-pill switch */}
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-[12px] text-stone-500">
            {ts('quickCalc.nisabBasis')}
          </span>
          <div
            role="tablist"
            aria-label={ts('quickCalc.nisabBasis')}
            className="inline-flex rounded-full border border-stone-300 bg-stone-50 p-0.5"
          >
            {['silver', 'gold'].map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={nisabStandard === k}
                data-testid={`quick-nisab-${k}`}
                onClick={() => setNisabStandard(k)}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${
                  nisabStandard === k
                    ? 'bg-[#EDF5F1] text-[#0D1520]'
                    : 'text-stone-600 hover:text-[#0D1520]'
                }`}
              >
                {k === 'silver' ? ts('quickCalc.nisabSilver') : ts('quickCalc.nisabGold')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Result strip */}
      <div
        data-testid="quick-result"
        className={`px-4 py-3 border-t ${
          results.isEligible
            ? 'bg-gradient-to-br from-[#EDF5F1] to-[#EDF5F1] text-[#0D1520] border-gold-500/40'
            : 'bg-stone-50 text-stone-700 border-stone-200'
        }`}
      >
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <p className={`text-[10.5px] font-mono uppercase tracking-[0.22em] font-semibold ${
              results.isEligible ? 'text-gold-600' : 'text-stone-500'
            }`}>
              {results.isEligible ? ts('quickCalc.dueAt').replace('{rate}', results.zakatRate) : ts('quickCalc.status')}
            </p>
            <p
              data-testid="quick-result-amount"
              className={`font-mono font-bold tabular-nums leading-none tracking-tight mt-1.5 ${
                results.isEligible
                  ? 'text-emerald-700 text-4xl sm:text-5xl'
                  : 'text-stone-800 text-2xl sm:text-3xl'
              }`}
            >
              {results.isEligible
                ? formatCurrency(results.zakatAmount)
                : ts('quickCalc.belowNisab')}
            </p>
          </div>
          <div className="text-end shrink-0">
            <p className={`text-[11px] font-medium ${
              results.isEligible ? 'text-[#8B8172]' : 'text-stone-500'
            }`}>
              {ts('quickCalc.nisabLabel')}
            </p>
            <p className={`text-[12px] font-semibold tabular-nums ${
              results.isEligible ? 'text-[#0D1520]' : 'text-stone-700'
            }`}>
              {formatCurrency(results.nisab)}
            </p>
          </div>
        </div>

        {/* Net zakatable meta line */}
        <p className={`mt-2 text-[11px] leading-snug ${
          results.isEligible ? 'text-[#4A5568]' : 'text-stone-500'
        }`}>
          {ts('quickCalc.netZakatable')}: <span className="tabular-nums font-semibold">{formatCurrency(results.netWorth)}</span>
          <span className="mx-1.5">·</span>
          {ts('quickCalc.assets')} <span className="tabular-nums">{formatCurrency(results.totalAssets)}</span>
          <span className="mx-1.5">·</span>
          {ts('quickCalc.debtsShort')} <span className="tabular-nums">{formatCurrency(results.totalDeductions)}</span>
        </p>
      </div>

      {/* CTA row */}
      <div className="px-4 py-3 border-t border-stone-200 bg-white flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 text-[10.5px] text-stone-500 min-w-0">
          <ShieldCheck className="w-3.5 h-3.5 text-[#0D4733] shrink-0" />
          <span className="truncate">{ts('quickCalc.privacy')}</span>
        </div>
        <button
          type="button"
          onClick={onOpenFull}
          data-testid="quick-open-full"
          className="inline-flex items-center gap-1 h-9 px-3.5 rounded-full bg-[#EDF5F1] hover:bg-[#EDF5F1] text-[#0D1520] text-[12px] font-bold transition-colors shrink-0"
        >
          {ts('quickCalc.openFull')}
          <ChevronRight className={`w-3.5 h-3.5 ${isRTL ? 'rotate-180' : ''}`} />
        </button>
      </div>
    </div>
  );
};

// Small internal input primitive — labelled, currency-aware, RTL-friendly.
const QuickField = ({
  testid, label, symbol, suffix, value, onChange, isRTL, step = '1', tone,
}) => {
  const isCurrency = Boolean(symbol);
  return (
    <label className="block">
      <span className={`block text-[12px] font-medium mb-1 ${
        tone === 'deduct' ? 'text-rose-700/90' : 'text-stone-600'
      }`}>
        {label}
      </span>
      <div className="relative">
        {isCurrency && (
          <span className={`absolute top-1/2 -translate-y-1/2 text-stone-500 text-[13px] ${
            isRTL ? 'end-2.5' : 'start-2.5'
          }`}>
            {symbol}
          </span>
        )}
        <input
          data-testid={testid}
          type="number" inputMode="decimal"
          min="0"
          step={step}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0"
          className={`w-full h-10 bg-stone-50 border border-stone-300 rounded-lg text-[14px] font-medium text-[#0D1520] tabular-nums focus:outline-none focus:ring-2 focus:ring-[#0D4733]/40 focus:border-[#0D4733] transition-colors ${
            isCurrency ? (isRTL ? 'pe-8 ps-3' : 'ps-8 pe-3') : 'px-3'
          } ${suffix ? (isRTL ? 'ps-8' : 'pe-8') : ''}`}
        />
        {suffix && (
          <span className={`absolute top-1/2 -translate-y-1/2 text-stone-500 text-[12px] ${
            isRTL ? 'start-2.5' : 'end-2.5'
          }`}>
            {suffix}
          </span>
        )}
      </div>
    </label>
  );
};

export default QuickZakatCalculator;
